<template>
  <a-row :gutter="24">
    <a-col :md="24">
      <a-card :bordered="false">
        <!-- ========== 顶部：标题 + 归属选择 + 搜索 + Tab ========== -->
        <div class="mall-header">
          <span class="mall-title">{{ modeTitle }}</span>
          <a-select
            v-if="mode !== 'qgd'"
            v-model="ownerId"
            showSearch
            optionFilterProp="children"
            :placeholder="mode === 'cgdd' ? '请选择供应商（仅显示其归属商品）' : '请选择上级供应商（显示其可售商品）'"
            style="width: 260px"
            @change="onOwnerChange">
            <a-select-option v-for="o in ownerOptions" :key="o.id" :value="o.id">{{ o.name }}</a-select-option>
          </a-select>
          <a-input-search
            v-model="searchKeyword"
            placeholder="商品名称搜索"
            style="width: 240px"
            @search="onSearch" />
          <a-radio-group v-model="activeTab" @change="onTabChange">
            <a-radio-button v-for="t in tabs" :key="t.key" :value="t.key">{{ t.name }}</a-radio-button>
          </a-radio-group>
        </div>

        <!-- 未选归属引导（cgdd/b2b） -->
        <div v-if="mode !== 'qgd' && !ownerId" class="mall-empty">
          <a-icon type="shop" style="font-size: 40px; color: #ccc" />
          <p>{{ mode === 'cgdd' ? '请先选择供应商，进入后仅显示该供应商归属的商品' : '请先选择绑定的上级供应商，进入后显示上级可售商品' }}</p>
        </div>

        <a-row v-else :gutter="16">
          <!-- ========== 左侧：分类 ========== -->
          <a-col :span="4">
            <div class="mall-category">
              <div class="mall-cat-item" :class="{ active: activeCategory === '' }" @click="onCategoryChange('')">全部</div>
              <div v-for="c in categories" :key="c.id"
                   class="mall-cat-item" :class="{ active: String(activeCategory) === String(c.id) }"
                   @click="onCategoryChange(c.id)">{{ c.name }}</div>
            </div>
          </a-col>

          <!-- ========== 主区：商品列表 ========== -->
          <a-col :span="14">
            <a-table
              size="middle"
              bordered
              rowKey="id"
              :columns="goodsColumns"
              :dataSource="goods"
              :pagination="false"
              :loading="goodsLoading"
              :scroll="{ y: 420 }">
              <template slot="img" slot-scope="text, record">
                <img v-if="record.imgUrl" :src="record.imgUrl"
                     :class="['mall-goods-img', { 'mall-goods-img-click': record.imgUrls && record.imgUrls.length }]"
                     alt="" @click="openPreview(record)" />
                <span v-else>-</span>
              </template>
              <template slot="price" slot-scope="text, record">
                <span v-if="!priceHidden">{{ record.priceText }}</span>
                <span v-else>-</span>
              </template>
              <template slot="action" slot-scope="text, record">
                <div class="mall-stepper">
                  <a-icon type="minus" :class="['mall-step-btn', { off: !cartNumberOf(record) }]" @click="onMinus(record)" />
                  <input
                    class="mall-step-input"
                    inputmode="decimal"
                    type="text"
                    :value="record.inputNumber"
                    @input="onNumInput(record, $event)"
                    @blur="onNumBlur(record)" />
                  <a-icon type="plus" class="mall-step-btn" @click="onPlus(record)" />
                </div>
              </template>
            </a-table>
            <a-pagination
              style="margin-top: 12px; text-align: right"
              :current="pagination.current"
              :pageSize="pagination.pageSize"
              :total="pagination.total"
              :showTotal="(t) => '共 ' + t + ' 条'"
              @change="onPageChange" />
          </a-col>

          <!-- ========== 右侧：已选清单 ========== -->
          <a-col :span="6">
            <a-card size="small" :bodyStyle="{ padding: '8px' }">
              <template slot="title">
                已选清单<span class="mall-cart-count">（已选 {{ cartCount }} 种）</span>
              </template>
              <div v-if="cartItems.length === 0" class="mall-cart-empty">暂未选择商品</div>
              <div v-for="item in cartItems" :key="item.materialId" class="mall-cart-item">
                <div class="mall-cart-name" :title="item.name + (item.standard ? '（' + item.standard + '）' : '')">
                  {{ item.name }}<span v-if="item.standard">（{{ item.standard }}）</span>
                </div>
                <div class="mall-cart-line">
                  <div class="mall-stepper">
                    <a-icon type="minus" :class="['mall-step-btn', { off: !item.number }]" @click="onCartMinus(item)" />
                    <input
                      class="mall-step-input"
                      inputmode="decimal"
                      type="text"
                      :value="item.numberText"
                      @input="onCartInput(item, $event)"
                      @blur="onCartBlur(item)" />
                    <a-icon type="plus" class="mall-step-btn" @click="onCartPlus(item)" />
                  </div>
                  <span class="mall-cart-unit">{{ item.unit }}</span>
                  <span v-if="!priceHidden" class="mall-cart-amount">¥{{ item.amountText }}</span>
                  <a-icon type="delete" class="mall-cart-del" @click="removeFromCart(item)" />
                </div>
              </div>
              <div v-if="cartItems.length > 0" class="mall-cart-footer">
                <span v-if="!priceHidden">合计：<span class="mall-cart-total">¥{{ cartTotalText }}</span></span>
                <a-button type="primary" icon="check" style="float: right" @click="openConfirm">提交请购</a-button>
              </div>
            </a-card>
          </a-col>
        </a-row>

        <!-- 提交确认弹窗 -->
        <a-modal
          title="确认提交"
          :visible="confirmVisible"
          :confirmLoading="submitting"
          :maskClosable="false"
          @ok="handleSubmit"
          @cancel="confirmVisible = false">
          <a-table
            size="small"
            bordered
            rowKey="materialId"
            :columns="confirmColumns"
            :dataSource="confirmItems"
            :pagination="false">
            <template slot="number" slot-scope="text, item">
              <div class="mall-stepper">
                <a-icon type="minus" :class="['mall-step-btn', { off: !item.number }]" @click="onConfirmMinus(item)" />
                <input
                  class="mall-step-input"
                  inputmode="decimal"
                  type="text"
                  :value="item.numberText"
                  @input="onConfirmInput(item, $event)"
                  @blur="onConfirmBlur(item)" />
                <a-icon type="plus" class="mall-step-btn" @click="onConfirmPlus(item)" />
              </div>
            </template>
          </a-table>
          <div v-if="!priceHidden" class="mall-confirm-total">合计：¥{{ confirmTotalText }}</div>
        </a-modal>

        <!-- 商品大图预览：多图左右切换 + 点击放大 -->
        <a-modal :visible="previewVisible" :footer="null" :width="860"
                 :bodyStyle="{ padding: '0', background: '#000' }" @cancel="closePreview">
          <div class="mall-preview">
            <div class="mall-preview-count">{{ previewIndex + 1 }} / {{ previewList.length }}</div>
            <span v-if="previewList.length > 1" class="mall-preview-arrow left" @click="prevPreview">‹</span>
            <div class="mall-preview-body" @click="toggleZoom">
              <img :src="previewList[previewIndex]" alt=""
                   :class="['mall-preview-img', { zoomed: previewZoom }]" />
            </div>
            <span v-if="previewList.length > 1" class="mall-preview-arrow right" @click="nextPreview">›</span>
          </div>
        </a-modal>
      </a-card>
    </a-col>
  </a-row>
</template>
<script>
import { getMallMaterials, getMallCategories, getMallSuppliers, createMallOrder, getSupplierArchives } from '@/api/plugin/orderReceive'
import { getFileAccessHttpUrl } from '@/api/manage'

const MODE_MAP = {
  '/plugin/order-receive/mall-qgd': { mode: 'qgd', title: '商城请购', scope: 'all' },
  '/plugin/order-receive/mall-purchase': { mode: 'cgdd', title: '按供应商采购', scope: 'supplier' },
  '/plugin/order-receive/mall-shop': { mode: 'b2b', title: '商城', scope: 'partner' }
}

function money(n) {
  return (Math.round((Number(n) || 0) * 100) / 100).toFixed(2)
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100
}

export default {
  name: 'MallEntry',
  data() {
    return {
      mode: 'qgd',
      modeTitle: '商城请购',
      scope: 'all',
      // 归属（cgdd=供应商档案id / b2b=伙伴租户id）
      ownerId: undefined,
      ownerOptions: [],
      // 筛选
      searchKeyword: '',
      search: '',
      activeTab: 'user',
      activeCategory: '',
      categories: [],
      // 商品
      goods: [],
      goodsLoading: false,
      priceHidden: false,
      showImg: false,
      placeholderImg: '',
      pagination: { current: 1, pageSize: 20, total: 0 },
      // 已选清单（唯一数据源，localStorage 持久化）
      cartItems: [],
      // 提交
      confirmVisible: false,
      confirmItems: [],
      submitting: false,
      // 大图预览（多图切换 + 点击放大）
      previewVisible: false,
      previewList: [],
      previewIndex: 0,
      previewZoom: false
    }
  },
  computed: {
    /** 缓存键：按 模式+归属 隔离，各供应商/伙伴的车互不影响 */
    storageKey() {
      return 'MALL_CART_' + this.mode + '_' + (this.ownerId || 'all')
    },
    /** 已选种类数 */
    cartCount() {
      return this.cartItems.length
    },
    tabs() {
      if (this.mode === 'b2b') {
        return [
          { key: 'user', name: '我的历史' },
          { key: '', name: '全部商品' },
          { key: 'tenant', name: '租户历史' }
        ]
      }
      return [
        { key: 'user', name: '我的常用' },
        { key: '', name: '全部商品' },
        { key: 'tenant', name: '本租户常购' }
      ]
    },
    goodsColumns() {
      const cols = []
      if (this.showImg) {
        cols.push({ title: '图片', key: 'img', width: 70, align: 'center', scopedSlots: { customRender: 'img' } })
      }
      cols.push({ title: '商品名称', dataIndex: 'name', width: 180 })
      cols.push({ title: '型号', dataIndex: 'standard', width: 110 })
      if (!this.priceHidden) {
        cols.push({ title: '单价(¥)', key: 'price', width: 90, align: 'right', scopedSlots: { customRender: 'price' } })
      }
      cols.push({ title: '单位', dataIndex: 'unit', width: 60, align: 'center' })
      cols.push({ title: '数量', key: 'action', width: 160, align: 'center', scopedSlots: { customRender: 'action' } })
      return cols
    },
    confirmColumns() {
      const cols = [
        { title: '商品', dataIndex: 'name' },
        { title: '数量', key: 'number', width: 170, align: 'center', scopedSlots: { customRender: 'number' } }
      ]
      if (!this.priceHidden) {
        cols.push({ title: '单价', dataIndex: 'priceText', width: 90, align: 'right' })
        cols.push({ title: '小计', dataIndex: 'amountText', width: 90, align: 'right' })
      }
      return cols
    },
    cartTotalText() {
      let total = 0
      this.cartItems.forEach((i) => { total += Number(i.amount) || 0 })
      return money(total)
    },
    confirmTotalText() {
      let total = 0
      this.confirmItems.forEach((i) => { total += (Number(i.price) || 0) * (Number(i.number) || 0) })
      return money(total)
    }
  },
  mounted() {
    this.initMode()
  },
  watch: {
    // 三路由复用同一组件实例：切换菜单时不重新 mounted，须监听路由重置模式与缓存
    '$route.path'() {
      this.initMode()
    }
  },
  methods: {
    /** 按当前路由初始化模式（mounted 与路由切换共用）：重置筛选/列表后按入口恢复各自缓存 */
    initMode() {
      const cfg = MODE_MAP[this.$route.path]
      if (cfg) {
        this.mode = cfg.mode
        this.modeTitle = cfg.title
        this.scope = cfg.scope
      }
      this.ownerId = undefined
      this.ownerOptions = []
      this.searchKeyword = ''
      this.search = ''
      this.activeTab = 'user'
      this.activeCategory = ''
      this.categories = []
      this.goods = []
      this.pagination.current = 1
      this.pagination.total = 0
      this.cartItems = []
      this.confirmVisible = false
      if (this.mode !== 'qgd') {
        this.loadOwnerOptions()
      } else {
        this.restoreCart()
        this.loadCategories()
        this.loadGoods()
      }
    },
    /** ========== 购物车缓存 ========== */
    saveCart() {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(this.cartItems))
      } catch (e) {
        // 存储异常不阻断主流程
      }
    },
    restoreCart() {
      let items = []
      try {
        const raw = localStorage.getItem(this.storageKey)
        if (raw) {
          const parsed = JSON.parse(raw)
          if (Array.isArray(parsed)) { items = parsed }
        }
      } catch (e) {
        items = []
      }
      this.cartItems = items
      this.refreshCartAmounts()
    },
    /** 归属下拉：cgdd=供应商档案 / b2b=绑定伙伴 */
    loadOwnerOptions() {
      if (this.mode === 'cgdd') {
        getSupplierArchives({}).then((res) => {
          if (res.code === 200) {
            this.ownerOptions = (res.data || []).map((s) => ({ id: s.id, name: s.supplierName }))
          }
        })
      } else {
        getMallSuppliers().then((res) => {
          if (res.code === 200) {
            this.ownerOptions = (res.data || []).map((s) => ({ id: s.partnerTenantId, name: s.name }))
          }
        })
      }
    },
    onOwnerChange() {
      this.activeCategory = ''
      this.restoreCart()
      this.loadCategories()
      this.loadGoods()
    },
    loadCategories() {
      const params = { scope: this.scope }
      if (this.mode === 'cgdd') { params.supplierId = this.ownerId }
      if (this.mode === 'b2b') { params.partnerTenantId = this.ownerId }
      getMallCategories(params).then((res) => {
        if (res.code === 200) {
          this.categories = res.data || []
          this.activeCategory = ''
        }
      })
    },
    onCategoryChange(id) {
      this.activeCategory = id
      this.loadGoods()
    },
    onTabChange() {
      this.loadGoods()
    },
    onSearch() {
      this.search = this.searchKeyword || ''
      this.loadGoods()
    },
    onPageChange(page) {
      this.pagination.current = page
      this.loadGoods()
    },
    loadGoods() {
      if (this.mode !== 'qgd' && !this.ownerId) { return }
      const p = this.pagination
      const params = {
        scope: this.scope,
        history: this.activeTab,
        search: this.search || undefined,
        categoryId: this.activeCategory === '' ? undefined : this.activeCategory,
        currentPage: p.current,
        pageSize: p.pageSize
      }
      if (this.mode === 'cgdd') { params.supplierId = this.ownerId }
      if (this.mode === 'b2b') { params.partnerTenantId = this.ownerId }
      this.goodsLoading = true
      getMallMaterials(params).then((res) => {
        if (res.code === 200 && res.data) {
          const rows = res.data.rows || []
          const showImg = !!res.data.showImg
          const placeholderImg = res.data.placeholderImg || ''
          rows.forEach((r) => {
            r.priceText = r.price == null ? '' : money(r.price)
            // 缩略图：img_name 第一张（imgUrls[0]）；全部图 imgUrls 供大图预览多图切换；无图用占位图
            r.imgUrls = (r.imgNames || []).map(p => getFileAccessHttpUrl('systemConfig/static/' + p))
            r.imgUrl = showImg
              ? (r.imgUrls.length
                ? r.imgUrls[0]
                : (placeholderImg ? placeholderImg : ''))
              : ''
            // 回填已缓存的数量：切分类/翻页不丢
            const exist = this.cartItems.find((i) => i.materialId === r.id)
            const n = exist ? Number(exist.number) || 0 : 0
            r.inputNumber = n > 0 ? String(n) : ''
          })
          this.goods = rows
          this.pagination.total = res.data.total || 0
          this.priceHidden = !!res.data.priceHidden
          this.showImg = showImg
          this.placeholderImg = placeholderImg
        } else {
          this.$message.warning(res.message || '查询失败')
        }
      }).catch(() => {
        this.$message.warning('查询失败')
      }).finally(() => {
        this.goodsLoading = false
      })
    },
    /** ========== 大图预览（多图切换 + 点击放大） ========== */
    openPreview(record) {
      if (!record.imgUrls || !record.imgUrls.length) { return } // 占位图/无图不弹
      this.previewList = record.imgUrls
      this.previewIndex = 0
      this.previewZoom = false
      this.previewVisible = true
    },
    closePreview() {
      this.previewVisible = false
    },
    prevPreview() {
      if (!this.previewList.length) { return }
      this.previewIndex = (this.previewIndex - 1 + this.previewList.length) % this.previewList.length
      this.previewZoom = false
    },
    nextPreview() {
      if (!this.previewList.length) { return }
      this.previewIndex = (this.previewIndex + 1) % this.previewList.length
      this.previewZoom = false
    },
    toggleZoom() {
      this.previewZoom = !this.previewZoom
    },
    /** ========== 购物车操作（cartItems 唯一数据源） ========== */
    cartNumberOf(record) {
      const exist = this.cartItems.find((i) => i.materialId === record.id)
      return exist ? (Number(exist.number) || 0) : 0
    },
    /** 数量写入：>0 更新/入车，0/空移除；goods 行同步显示 */
    applyNumber(record, n) {
      n = round2(n)
      const idx = this.cartItems.findIndex((i) => i.materialId === record.id)
      if (n > 0) {
        if (idx > -1) {
          this.cartItems[idx].number = n
        } else {
          this.cartItems.push({
            materialId: record.id,
            name: record.name,
            standard: record.standard || '',
            unit: record.unit || '',
            price: record.price == null ? 0 : Number(record.price),
            priceText: record.priceText,
            number: n
          })
        }
      } else if (idx > -1) {
        this.cartItems.splice(idx, 1)
      }
      // 回填商品行输入框
      const row = this.goods.find((g) => g.id === record.id)
      if (row) { row.inputNumber = n > 0 ? String(n) : '' }
      this.refreshCartAmounts()
      this.saveCart()
    },
    onMinus(record) {
      const n = this.cartNumberOf(record)
      if (n > 0) { this.applyNumber(record, n - 1) }
    },
    onPlus(record) {
      this.applyNumber(record, this.cartNumberOf(record) + 1)
    },
    onNumInput(record, e) {
      const v = e.target.value
      const n = parseFloat(v)
      // 输入中允许空串暂存（不清车，失焦时规整）
      if (v === '' || v === '.') { return }
      if (isNaN(n) || n < 0) { return }
      this.applyNumber(record, n)
    },
    onNumBlur(record) {
      const n = this.cartNumberOf(record)
      const row = this.goods.find((g) => g.id === record.id)
      if (row) { row.inputNumber = n > 0 ? String(n) : '' }
    },
    /** 清单行步进（直接操作 cartItems 项） */
    onCartMinus(item) {
      if (!(Number(item.number) > 0)) { return }
      this.applyCartItemNumber(item, round2(Number(item.number) - 1))
    },
    onCartPlus(item) {
      this.applyCartItemNumber(item, round2((Number(item.number) || 0) + 1))
    },
    onCartInput(item, e) {
      const v = e.target.value
      if (v === '' || v === '.') { return }
      const n = parseFloat(v)
      if (isNaN(n) || n < 0) { return }
      this.applyCartItemNumber(item, n)
    },
    onCartBlur(item) {
      const n = Number(item.number) || 0
      item.numberText = n > 0 ? String(n) : ''
    },
    applyCartItemNumber(item, n) {
      n = round2(n)
      if (n > 0) {
        item.number = n
        item.amount = (Number(item.price) || 0) * n
        item.amountText = money(item.amount)
        item.numberText = String(n)
      } else {
        const idx = this.cartItems.indexOf(item)
        if (idx > -1) { this.cartItems.splice(idx, 1) }
      }
      // 同步商品行显示
      const row = this.goods.find((g) => g.id === item.materialId)
      if (row) { row.inputNumber = n > 0 ? String(n) : '' }
      this.saveCart()
    },
    removeFromCart(item) {
      const idx = this.cartItems.indexOf(item)
      if (idx > -1) { this.cartItems.splice(idx, 1) }
      const row = this.goods.find((g) => g.id === item.materialId)
      if (row) { row.inputNumber = '' }
      this.saveCart()
    },
    refreshCartAmounts() {
      this.cartItems.forEach((i) => {
        i.amount = (Number(i.price) || 0) * (Number(i.number) || 0)
        i.amountText = money(i.amount)
        i.numberText = String(i.number)
        i.priceText = i.priceText || money(i.price)
      })
    },
    /** ========== 提交 ========== */
    openConfirm() {
      if (this.cartItems.length === 0) {
        this.$message.warning('请先填写商品数量')
        return
      }
      this.refreshCartAmounts()
      // 浅拷贝数组（元素同引用）：弹窗内修改实时同步清单与缓存
      this.confirmItems = this.cartItems.slice()
      this.confirmVisible = true
    },
    /** 确认弹窗内步进（同引用同步 cartItems，改 0 删行） */
    onConfirmMinus(item) {
      if (!(Number(item.number) > 0)) { return }
      this.applyConfirmNumber(item, round2(Number(item.number) - 1))
    },
    onConfirmPlus(item) {
      this.applyConfirmNumber(item, round2((Number(item.number) || 0) + 1))
    },
    onConfirmInput(item, e) {
      const v = e.target.value
      if (v === '' || v === '.') { return }
      const n = parseFloat(v)
      if (isNaN(n) || n < 0) { return }
      this.applyConfirmNumber(item, n)
    },
    onConfirmBlur(item) {
      const n = Number(item.number) || 0
      item.numberText = n > 0 ? String(n) : ''
    },
    applyConfirmNumber(item, n) {
      n = round2(n)
      if (n > 0) {
        item.number = n
        item.amount = (Number(item.price) || 0) * n
        item.amountText = money(item.amount)
        item.numberText = String(n)
      } else {
        // 改 0 即删除：弹窗与清单同步移除
        const ci = this.confirmItems.indexOf(item)
        if (ci > -1) { this.confirmItems.splice(ci, 1) }
        const ki = this.cartItems.indexOf(item)
        if (ki > -1) { this.cartItems.splice(ki, 1) }
      }
      const row = this.goods.find((g) => g.id === item.materialId)
      if (row) { row.inputNumber = n > 0 ? String(n) : '' }
      this.saveCart()
    },
    handleSubmit() {
      const items = this.confirmItems.map((i) => ({
        materialId: i.materialId,
        operNumber: i.number,
        // 屏蔽角色价格不可见：落单单价 0，由有权者在审核环节补价
        unitPrice: this.priceHidden ? 0 : (Number(i.price) || 0),
        remark: ''
      }))
      if (items.length === 0) {
        this.$message.warning('请先填写商品数量')
        return
      }
      const body = { mode: this.mode, items: items }
      if (this.mode === 'cgdd') { body.supplierId = this.ownerId }
      if (this.mode === 'b2b') { body.partnerTenantId = this.ownerId }
      this.submitting = true
      createMallOrder(body).then((res) => {
        if (res.code === 200) {
          this.confirmVisible = false
          // 请购单已生成，清空本地缓存车
          this.cartItems = []
          try { localStorage.removeItem(this.storageKey) } catch (e) { /* 忽略 */ }
          const data = res.data || {}
          const tip = data.creditTip
          if (tip) {
            this.showCreditTip(tip, '提交成功，单号 ' + (data.billNo || ''))
          } else {
            this.$message.success('提交成功，单号 ' + (data.billNo || ''))
          }
          this.loadGoods()
        } else {
          this.$message.warning(res.message || '提交失败')
        }
      }).catch((err) => {
        this.$message.warning((err && err.message) || '提交失败')
      }).finally(() => {
        this.submitting = false
      })
    },
    showCreditTip(tip, successMsg) {
      this.$info({
        title: '客户信用提示',
        content: this.$createElement('div', null, [
          this.$createElement('p', { style: 'color: #52c41a; margin-bottom: 8px;' }, successMsg),
          this.$createElement('p', { style: 'color: #fa8c16; font-weight: 600; white-space: pre-wrap; margin: 0;' }, tip)
        ]),
        okText: '知道了'
      })
    }
  }
}
</script>
<style scoped>
.mall-header { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; flex-wrap: wrap; }
.mall-title { font-size: 16px; font-weight: 600; }
.mall-empty { text-align: center; padding: 80px 0; color: #999; }
.mall-category { border: 1px solid #e8e8e8; border-radius: 4px; max-height: 480px; overflow-y: auto; }
.mall-cat-item { padding: 8px 12px; cursor: pointer; border-bottom: 1px solid #f0f0f0; }
.mall-cat-item:last-child { border-bottom: none; }
.mall-cat-item:hover { background: #f5f5f5; }
.mall-cat-item.active { background: #e6f7ff; color: #1890ff; font-weight: 600; }
.mall-cart-count { color: #999; font-size: 12px; font-weight: 400; }
.mall-cart-empty { text-align: center; color: #999; padding: 24px 0; }
.mall-cart-item { border-bottom: 1px solid #f0f0f0; padding: 6px 0; }
.mall-cart-name { font-size: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mall-cart-line { display: flex; align-items: center; gap: 6px; margin-top: 4px; }
.mall-cart-unit { color: #888; font-size: 12px; }
.mall-cart-amount { color: #f5222d; font-size: 12px; margin-left: auto; }
.mall-cart-del { color: #999; cursor: pointer; }
.mall-cart-del:hover { color: #f5222d; }
.mall-cart-footer { padding-top: 8px; overflow: hidden; }
.mall-cart-total { color: #f5222d; font-size: 16px; font-weight: 600; }
.mall-confirm-total { text-align: right; margin-top: 12px; font-size: 15px; }
.mall-confirm-total span { color: #f5222d; font-weight: 600; }
/* 数量步进器（- 输入框 +，对齐移动端） */
.mall-stepper { display: inline-flex; align-items: center; gap: 6px; }
.mall-step-btn { display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 24px; border: 1px solid #d9d9d9; border-radius: 50%; color: #1890ff; cursor: pointer; font-size: 12px; background: #fff; }
.mall-step-btn:hover { border-color: #1890ff; }
.mall-step-btn.off { color: #ccc; cursor: not-allowed; }
.mall-step-input { width: 64px; height: 26px; text-align: center; border: 1px solid #d9d9d9; border-radius: 3px; outline: none; font-size: 13px; }
.mall-step-input:focus { border-color: #1890ff; }
/* 商品缩略图（shop_img 开关开启时显示） */
.mall-goods-img { width: 40px; height: 40px; object-fit: cover; border-radius: 4px; background: #f5f5f5; }
.mall-goods-img-click { cursor: zoom-in; }
/* 大图预览弹窗（多图切换 + 点击放大） */
.mall-preview { position: relative; display: flex; align-items: center; justify-content: center; height: 70vh; background: #000; }
.mall-preview-count { position: absolute; top: 10px; right: 14px; z-index: 10; color: #fff; font-size: 13px; background: rgba(0, 0, 0, 0.5); border-radius: 10px; padding: 2px 10px; }
.mall-preview-body { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; overflow: auto; cursor: zoom-in; }
.mall-preview-img { max-width: 100%; max-height: 100%; transition: transform 0.2s; user-select: none; }
.mall-preview-img.zoomed { transform: scale(2); cursor: zoom-out; }
.mall-preview-arrow { position: absolute; top: 50%; transform: translateY(-50%); z-index: 10; width: 36px; height: 36px; line-height: 32px; text-align: center; font-size: 22px; color: #fff; background: rgba(0, 0, 0, 0.45); border-radius: 50%; cursor: pointer; }
.mall-preview-arrow:hover { background: rgba(0, 0, 0, 0.7); }
.mall-preview-arrow.left { left: 12px; }
.mall-preview-arrow.right { right: 12px; }
</style>
